import { Button, Heading, VStack, Text, Box, Link } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useContext, useState } from 'react';
import { AdditionalFacilitiesFormFields, AdditionalFacilitiesRequest } from 'src/@types';
import {
  AdditionalFacilitiesEmployerSection,
  AdditionalFacilitiesEmployeeSection,
  AdditionalFacilitiesLifeInsuranceSection,
  AdditionalFacilitiesInformationSection,
  AdditionalFacilitiesDisabilitySection,
  AdditionalFacilitiesFinalizeSection
} from './sections';
import { API } from '../../../../services';
import { getDefaultValues, prepareFormData } from './utils/additional-facilities-utils';
import { scrollTo } from '../../../../utils';
import { AppAlertDialog } from '../../../shared/general';
import { FormContext } from '../../../../context/form-context';
import { IntlProvider } from 'react-intl';

export const AdditionalFacilitiesForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty }
  } = useForm<AdditionalFacilitiesFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues()
  });

  const { isLoading, hasError, setLoading, setError } = useContext(FormContext);

  const values = watch();
  const [pdfUrl, setPdfUrl] = useState('#');
  const [activeStep, setActiveStep] = useState<1 | 2>(1);

  async function onSubmit(values: AdditionalFacilitiesFormFields) {
    setLoading(true);
    try {
      const res = await API.post<{ url: string }, AdditionalFacilitiesRequest>(
        'employerstatement/aanvullendevoorzieningen',
        prepareFormData(values)
      );
      setPdfUrl(res.url);
      setActiveStep(2);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
      scrollTo('.react-tools');
    }
  }

  const handleCloseAlert = () => {
    setError(false);
  };

  const Success = () => (
    <Box display={activeStep === 2 ? undefined : 'none'}>
      <Text mb={4}>Geachte werkgever,</Text>
      <Text mb={4}>
        Uw werknemer is bezig met de aanvraag voor een hypotheek en verzoekt u om bijgaande
        aanvullende voorzieningen in te vullen.
      </Text>
      <Text>
        <Link href={pdfUrl} isExternal>
          Download
        </Link>{' '}
        nu de de aanvullende en voorzieningen in pdf formaat. Of{' '}
        <Link onClick={() => setActiveStep(1)}>vul deze opnieuw in.</Link>
      </Text>
    </Box>
  );

  return (
    <>
      <IntlProvider locale={'nl'} defaultLocale={'nl'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack display={activeStep === 1 ? undefined : 'none'} spacing={4} align="flex-start">
            <Heading variant={'h1'} as={'h1'}>
              Online formulier Aanvullende Voorzieningen
            </Heading>
            <Box pb={2}>
              <Text>
                Geachte werkgever,<br></br>
                Uw werknemer is bezig met de aanvraag voor een hypotheek en verzoekt u om bijgaande
                aanvullendevoorzieningen in te vullen. Banken zijn streng in het controleren van
                deze verklaringen en eisen een nieuwe als iets niet goed of niet volledig is
                ingevuld. Om dat extra werk te voorkomen is het belangrijk dat u de verklaring
                volgens{' '}
                <Link
                  href={
                    'https://www.hypotheker.nl/online-aanvullendevoorzieningen/aanvullendevoorzieningen-richtlijnen/'
                  }
                  isExternal>
                  {' '}
                  de richtlijnen
                </Link>{' '}
                invult.
              </Text>
            </Box>
            <AppAlertDialog title={'let op!'} open={hasError} onClose={handleCloseAlert}>
              <Box>
                Door een technische storing kunnen we nu helaas geen aanvullendevoorzieningen
                genereren. Probeer het opnieuw of{' '}
                <Link
                  href={
                    'https://www.hypotheker.nl/media/4440/formulier-aanvullende-voorzieningen-werkgever2021.pdf'
                  }
                  isExternal={true}>
                  download de invulbare PDF.
                </Link>
              </Box>
            </AppAlertDialog>

            <Box py={8}>
              <AdditionalFacilitiesEmployerSection
                heading={'Gegevens werkgever'}
                register={register}
                values={values}
                errors={errors}></AdditionalFacilitiesEmployerSection>
            </Box>
            <Box py={8}>
              <AdditionalFacilitiesEmployeeSection
                heading={'Gegevens werknemer'}
                register={register}
                values={values}
                errors={errors}></AdditionalFacilitiesEmployeeSection>
            </Box>

            <Box py={8}>
              <AdditionalFacilitiesInformationSection
                heading={'Graag de hieronder gevraagde gegevens invullen.'}
                register={register}
                values={values}
                errors={errors}></AdditionalFacilitiesInformationSection>
            </Box>

            {values.informationRequestedCanNotBeProvidedToTheEmployee !== 'true' && (
              <Box>
                <Box py={8}>
                  <AdditionalFacilitiesLifeInsuranceSection
                    heading={'Overlijden'}
                    register={register}
                    values={values}
                    errors={errors}></AdditionalFacilitiesLifeInsuranceSection>
                </Box>

                <Box py={8}>
                  <AdditionalFacilitiesDisabilitySection
                    heading={'Arbeidsongeschiktheid'}
                    register={register}
                    values={values}
                    errors={errors}></AdditionalFacilitiesDisabilitySection>
                </Box>
              </Box>
            )}
            <Box py={8}>
              <AdditionalFacilitiesFinalizeSection
                heading={'Ondertekenen'}
                register={register}
                values={values}
                errors={errors}></AdditionalFacilitiesFinalizeSection>
            </Box>

            <Button
              isLoading={isLoading}
              loadingText={'Bezig met versturen'}
              type="submit"
              colorScheme="orange"
              isDisabled={false}>
              {!isValid && isDirty ? `Wat moet ik nog invullen?` : `Naar stap 2`}
            </Button>
          </VStack>
          <Success />
        </form>
      </IntlProvider>
    </>
  );
};
